// TIP-611 — section 1 of the order form, "Your details (the requester)".
// These five fields were hardcoded placeholders (REQUESTER_PROFILE, a fixture
// naming "Faraz Ahmed"). Now that the form POSTs to a real endpoint, a
// placeholder would file every brief under a person who is not the customer
// and send the analyst's questions to a stranger. They come from two reads:
//   GET /api/user/me/                          → name, designation, email, mobile
//   GET /api/company/company-details/<id>      → the company being acted as
// The company is the one selected in the dashboard switcher — the same company
// whose allowance is being spent — so the brief is requested in the name of
// the business that is paying for it.
// No React here, so the mapping can be pinned by a test without a store.
import { COUNTRY_LIST, DEFAULT_DIAL } from "./orderFormFixtures";
import { splitMobile } from "./requesterLookup";

/* The designation and the employer sit on the Individual record, which /api/user/me/ nests as an ARRAY (a user can hold more than one). */
const individualOf = (user) => {
  const list = user?.individual;
  return (Array.isArray(list) ? list[0] : list) || null;
};

const text = (v) => (typeof v === "string" ? v.trim() : "");

/* The requester's country, as a name the country picker can actually select. */
export const resolveCountry = (company) => {
  const addresses = Array.isArray(company?.companyAddress) ? company.companyAddress : [];
  // The primary address first: it is a country NAME, and it is the address the
  const primary = addresses.find((a) => a?.primary_address) || addresses[0] || null;
  const fromAddress = text(primary?.company_address_detailsID?.country);

  const byName = COUNTRY_LIST.find((c) => c.name.toLowerCase() === fromAddress.toLowerCase());
  if (byName) return byName;

  // Falling back to the ISO code on the company record, which is what the rest
  // of the dashboard reads for a company's country.
  const code = text(company?.country_code).toUpperCase();
  return COUNTRY_LIST.find((c) => c.code === code) || null;
};

/* The two payloads → the five fields section 1 fills in. */
export const mapRequesterProfile = ({ user, company } = {}) => {
  const individual = individualOf(user);
  const country = resolveCountry(company);
  const mobile = splitMobile(user?.mobile_number, country ? country.dial : DEFAULT_DIAL);

  return {
    fullName: text(user?.name),
    jobTitle: text(individual?.designation),
    // The company the brief is being ordered FOR, not whatever the user typed
    // as their employer when they registered. The employer name is the fallback
    // only because a company read can fail while the form is already open.
    company:
      text(company?.registered_company_name) || text(individual?.employer_company_name),
    email: text(user?.email),
    country: country ? country.name : "",
    // `mobile_number` is stored as one string and may or may not carry its
    // country code, so it is split the same way the reference lookup splits
    // its own: a `+971...` number would otherwise land whole in the number box
    // while the picker still said +971, sending the code twice.
    mobile: mobile.phone,
    // The dial follows the country, so the code and the country cannot
    // disagree — unless the stored number carried a code of its own, which is
    // the more specific answer about where that number actually is.
    dial: mobile.applies ? mobile.dial : country ? country.dial : DEFAULT_DIAL,
  };
};

/** The fields this profile is allowed to fill. Nothing else is touched. */
export const PROFILE_FIELDS = [
  "fullName",
  "jobTitle",
  "company",
  "email",
  "country",
  "mobile",
  "dial",
];
